import React from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import {
  Edit as EditIcon,
  WhatsApp as WhatsAppIcon,
} from '@mui/icons-material';
import {
  LEAD_ALERT_COLORS,
  LEAD_ALERT_LABELS,
  LEAD_CHANNEL_LABELS,
  LEAD_PAYMENT_METHOD_LABELS,
  LEAD_PRICE_RANGE_LABELS,
  LEAD_STATUS_COLORS,
  LEAD_STATUS_LABELS,
  Lead,
  LeadChannel,
  LeadPaymentMethod,
  LeadPriceRange,
} from '../../types';
import {
  buildLeadWhatsappMessage,
  calcDaysWithoutContact,
  formatDateBR,
  formatPhoneBR,
  getWhatsappLink,
  onlyDigits,
  resolveLeadAlert,
} from './leadUtils';

type FieldProps = { label: string; value?: React.ReactNode };

const Field: React.FC<FieldProps> = ({ label, value }) => (
  <Box>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body2" fontWeight={500} sx={{ wordBreak: 'break-word' }}>
      {value == null || value === '' ? '—' : value}
    </Typography>
  </Box>
);

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <Box sx={{ mb: 2.5 }}>
    <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.25 }}>
      {title}
    </Typography>
    <Grid container spacing={2}>
      {children}
    </Grid>
  </Box>
);

export interface LeadOverviewDialogProps {
  open: boolean;
  lead: Lead | null;
  sellerName?: string;
  canWrite?: boolean;
  onClose: () => void;
  onEdit?: (lead: Lead) => void;
}

const channelLabel = (v?: string | null) =>
  v ? LEAD_CHANNEL_LABELS[v as LeadChannel] ?? v : '—';

const LeadOverviewDialog: React.FC<LeadOverviewDialogProps> = ({
  open,
  lead,
  sellerName,
  canWrite,
  onClose,
  onEdit,
}) => {
  if (!lead) {
    return (
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
        <DialogTitle>Visão geral do lead</DialogTitle>
        <DialogContent>
          <Typography color="text.secondary">Nenhum lead selecionado.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Fechar</Button>
        </DialogActions>
      </Dialog>
    );
  }

  const days = calcDaysWithoutContact(lead);
  const alert = resolveLeadAlert(lead);
  const waHref = getWhatsappLink(onlyDigits(lead.whatsapp), buildLeadWhatsappMessage(lead));

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>
        <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
          <Typography component="span" variant="h6" fontWeight={700}>
            {lead.name}
          </Typography>
          <Chip
            size="small"
            label={LEAD_STATUS_LABELS[lead.status] ?? lead.status}
            color={LEAD_STATUS_COLORS[lead.status] ?? 'default'}
            variant="outlined"
          />
          {alert !== 'nenhum' ? (
            <Chip
              size="small"
              label={LEAD_ALERT_LABELS[alert]}
              color={LEAD_ALERT_COLORS[alert]}
            />
          ) : null}
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        <Section title="Identificação">
          <Grid item xs={12} sm={6} md={4}>
            <Field label="Nome" value={lead.name} />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Field label="WhatsApp" value={formatPhoneBR(lead.whatsapp)} />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Field label="E-mail" value={lead.email} />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Field label="Cidade / bairro" value={lead.city_neighborhood} />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Field label="Primeiro contato" value={formatDateBR(lead.first_contact_at)} />
          </Grid>
        </Section>

        <Divider sx={{ mb: 2 }} />

        <Section title="Origem">
          <Grid item xs={12} sm={4}>
            <Field label="Origem" value={channelLabel(lead.origin)} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Field label="Origem secundária" value={channelLabel(lead.secondary_origin)} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Field label="Canal atual" value={channelLabel(lead.current_channel)} />
          </Grid>
        </Section>

        <Divider sx={{ mb: 2 }} />

        <Section title="Interesse">
          <Grid item xs={12} sm={6}>
            <Field label="Veículo de interesse" value={lead.vehicle_interest} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Field
              label="Faixa de preço"
              value={
                lead.price_range
                  ? LEAD_PRICE_RANGE_LABELS[lead.price_range as LeadPriceRange] ?? lead.price_range
                  : '—'
              }
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Field label="Terá troca" value={lead.has_trade_in ? 'Sim' : 'Não'} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Field label="Veículo da troca" value={lead.trade_in_vehicle} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Field
              label="Forma de pagamento"
              value={
                lead.payment_method
                  ? LEAD_PAYMENT_METHOD_LABELS[lead.payment_method as LeadPaymentMethod] ??
                    lead.payment_method
                  : '—'
              }
            />
          </Grid>
        </Section>

        <Divider sx={{ mb: 2 }} />

        <Section title="Funil / Gestão">
          <Grid item xs={12} sm={4}>
            <Field
              label="Status"
              value={
                <Chip
                  size="small"
                  label={LEAD_STATUS_LABELS[lead.status] ?? lead.status}
                  color={LEAD_STATUS_COLORS[lead.status] ?? 'default'}
                  variant="outlined"
                />
              }
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Field label="Vendedor" value={sellerName || lead.seller?.name || '—'} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Field
              label="Alerta"
              value={
                alert === 'nenhum' ? (
                  '—'
                ) : (
                  <Chip size="small" label={LEAD_ALERT_LABELS[alert]} color={LEAD_ALERT_COLORS[alert]} />
                )
              }
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Field label="Último contato" value={formatDateBR(lead.last_contact_at)} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Field label="Próximo contato" value={formatDateBR(lead.next_contact_at)} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Field label="Ação do próximo contato" value={lead.next_contact_action} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Field label="Dias sem contato" value={days == null ? '—' : days} />
          </Grid>
        </Section>

        <Divider sx={{ mb: 2 }} />

        <Section title="Pós-venda">
          <Grid item xs={12} sm={4}>
            <Field label="Veículo comprado" value={lead.purchased_vehicle} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Field label="Data da compra" value={formatDateBR(lead.purchase_date)} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Field
              label="Previsão troca / aniversário"
              value={formatDateBR(lead.trade_forecast_or_birthday)}
            />
          </Grid>
        </Section>

        <Divider sx={{ mb: 2 }} />

        <Section title="Observações">
          <Grid item xs={12}>
            <Field
              label="Observações"
              value={
                lead.notes ? (
                  <Typography variant="body2" whiteSpace="pre-wrap">
                    {lead.notes}
                  </Typography>
                ) : (
                  '—'
                )
              }
            />
          </Grid>
        </Section>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, justifyContent: 'space-between' }}>
        <Button onClick={onClose}>Fechar</Button>
        <Stack direction="row" spacing={1}>
          <Button
            color="success"
            startIcon={<WhatsAppIcon />}
            component="a"
            href={waHref}
            target="_blank"
            rel="noopener noreferrer"
          >
            WhatsApp
          </Button>
          {canWrite && onEdit ? (
            <Button
              variant="contained"
              startIcon={<EditIcon />}
              onClick={() => onEdit(lead)}
            >
              Editar
            </Button>
          ) : null}
        </Stack>
      </DialogActions>
    </Dialog>
  );
};

export default LeadOverviewDialog;
